import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import AiCastShowcase from "../components/AiCastShowcase";
import "./Landing.css";

const DEMO_NATIVE_W = 1280;

export default function Landing(): JSX.Element {
  // Scale the demo iframe so its 1280px-wide internal layout fits whatever
  // width the container ends up at (full-bleed on mobile, ~520px desktop).
  const previewRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const apply = (): void => {
      const w = el.clientWidth;
      if (w <= 0) return;
      // Cap minimum scale at 0.35 so the HUD stays legible on narrow phones
      // instead of collapsing into unreadable pixels.
      const scale = Math.max(0.35, w / DEMO_NATIVE_W);
      el.style.setProperty("--demo-scale", String(scale));
    };
    // Set immediately on mount so there's no full-size flash before
    // ResizeObserver fires its first callback.
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="lp">
      <section className="lp-hero">
        <div className="lp-hero-inner">
          <div className="lp-hero-pill">For Twitch &amp; YouTube creators</div>
          <h1>
            Donations<br />that <span className="lp-glow">live forever</span>.
          </h1>
          <p className="lp-lead">
            Every supporter becomes a character that lives on your public wall —
            with their name, message, and voice — long after the alert fades.
            Streamlabs gives streamers a 12-second alert. SubsWall gives them a monument.
          </p>
          <div className="lp-ctas">
            <Link to="/login" className="lp-btn-xl">Claim your wall</Link>
            <Link to="/demo" className="lp-btn-ghost">See a live wall</Link>
          </div>
          <div className="lp-trust">
            4.9% flat · No surprise fees · 1-click cancel · Twitch + Google login
          </div>
        </div>
        <div className="lp-preview lp-preview-live" aria-label="Live overlay demo" ref={previewRef}>
          <iframe
            title="SubsWall live overlay demo"
            src="/concept/living-cast"
            className="lp-preview-iframe"
            loading="lazy"
          />
          <div className="lp-preview-caption">
            <span className="lp-pv-live">● LIVE DEMO</span>
          </div>
        </div>
      </section>

      <section className="lp-section">
        <div className="lp-section-head">
          <div className="lp-eyebrow">How it works</div>
          <h2>Set up once. <span className="lp-grad">Live in a minute.</span></h2>
        </div>
        <div className="lp-mech">
          <div className="lp-mech-card" style={{ ["--c" as string]: "#FF1744", ["--g" as string]: "#FF174466" }}>
            <div className="lp-step">Step 1</div>
            <h3>Sign in</h3>
            <p>Sign in with Twitch or Google. Five seconds. No install, no card needed.</p>
          </div>
          <div className="lp-mech-card" style={{ ["--c" as string]: "#9146FF", ["--g" as string]: "#9146FF66" }}>
            <div className="lp-step">Step 2</div>
            <h3>Share your wall link</h3>
            <p>Paste <em>subswall.com/yourhandle</em> in your Twitch panels, video descriptions, or bio. The wall is live.</p>
          </div>
          <div className="lp-mech-card" style={{ ["--c" as string]: "#76FF03", ["--g" as string]: "#76FF0366" }}>
            <div className="lp-step">Step 3</div>
            <h3>Supporters land forever</h3>
            <p>Every tip becomes a character with a message that stays on your wall — visible to every viewer who ever visits. The wall doesn't disappear when the stream ends.</p>
          </div>
        </div>
      </section>

      <section className="lp-section">
        <div className="lp-section-head">
          <div className="lp-eyebrow">Why streamers use it</div>
          <h2>A monument, <span className="lp-grad">not an alert.</span></h2>
        </div>
        <div className="lp-why">
          <div className="lp-why-row" style={{ ["--c" as string]: "#9146FF", ["--g" as string]: "#9146FF66" }}>
            <div className="lp-icon">♛</div>
            <div>
              <h4>The wall doesn't end</h4>
              <p>Every supporter lives on your public profile at subswall.com/<em>yourhandle</em> forever. Their character, their message, their tip — visible to every viewer who ever visits your page.</p>
            </div>
          </div>
          <div className="lp-why-row" style={{ ["--c" as string]: "#FF1744", ["--g" as string]: "#FF174466" }}>
            <div className="lp-icon">▣</div>
            <div>
              <h4>Supporters get an identity</h4>
              <p>Each tipper picks a character + leaves a message. They're not anonymous — they're cast members on your channel. Returning supporters keep the same character every time.</p>
            </div>
          </div>
          <div className="lp-why-row" style={{ ["--c" as string]: "#76FF03", ["--g" as string]: "#76FF0366" }}>
            <div className="lp-icon">⚡</div>
            <div>
              <h4>Optional on-stream strip</h4>
              <p>Want the cast walking across the bottom of your stream? Add the Live Strip browser source to OBS. Same characters as your wall, live during broadcast. Optional — not required.</p>
            </div>
          </div>
          <div className="lp-why-row" style={{ ["--c" as string]: "#2979FF", ["--g" as string]: "#2979FF66" }}>
            <div className="lp-icon">$</div>
            <div>
              <h4>4.9% — that's it</h4>
              <p>Flat fee per tip. No subscriptions, no surprise yearly charges, no hidden viewer enrollments. Stripe processing passed to the tipper. Cancel in one click, refund within 30 days.</p>
            </div>
          </div>
          <div className="lp-why-row" style={{ ["--c" as string]: "#FFEA00", ["--g" as string]: "#FFEA0066" }}>
            <div className="lp-icon">●</div>
            <div>
              <h4>Zero install to start</h4>
              <p>The wall works the moment you sign up. No OBS plugin, no desktop app, no setup. Paste your link in Twitch panels and you're live.</p>
            </div>
          </div>
          <div className="lp-why-row" style={{ ["--c" as string]: "#D500F9", ["--g" as string]: "#D500F966" }}>
            <div className="lp-icon">⌬</div>
            <div>
              <h4>Coming soon: AI voice + character</h4>
              <p>Every supporter will get their own AI-generated character and voice — permanently theirs. When they tip, their character speaks their message aloud. Returning supporters keep their voice forever.</p>
            </div>
          </div>
        </div>
      </section>

      <AiCastShowcase />

      <section className="lp-section">
        <div className="lp-section-head">
          <div className="lp-eyebrow">FAQ</div>
          <h2>Questions, <span className="lp-grad">answered.</span></h2>
        </div>
        <div className="lp-faq">
          <details>
            <summary>What's the take rate?</summary>
            <p>A flat 4.9% per tip. Stripe processing is passed to the tipper (industry standard). No subscriptions for you, no surprise renewals, no enrolling your viewers in anything. Cancel in one click, refund within 30 days, no questions.</p>
          </details>
          <details>
            <summary>Do I need to install anything?</summary>
            <p>No. The wall works the moment you sign up — just share <em>subswall.com/yourhandle</em>. If you also want the cast walking across the bottom of your stream, you can add an optional browser source to OBS, but it's not required.</p>
          </details>
          <details>
            <summary>How do my viewers pay?</summary>
            <p>Card, Apple Pay, or Google Pay via Stripe Checkout. Minimum tip is $5.</p>
          </details>
          <details>
            <summary>What does the wall look like?</summary>
            <p>Every supporter is a character with their name, tip amount, and message. They walk around your public profile page at <em>subswall.com/yourhandle</em>. Bigger tips = bigger characters. Click any character to see their full history — every tip they've ever sent you.</p>
          </details>
          <details>
            <summary>Do I have to ditch Streamlabs?</summary>
            <p>No. Keep your existing alerts. SubsWall is the persistent supporter layer — the wall that doesn't disappear. Runs alongside everything else.</p>
          </details>
          <details>
            <summary>What about AI?</summary>
            <p>Coming next: each supporter will get a unique AI-generated character and voice locked to them forever. When they tip, their character speaks their message in their own voice. Returning supporters get callbacks. That's the v2 release.</p>
          </details>
          <details>
            <summary>When do I get paid?</summary>
            <p>Stripe payouts hit your bank in two business days. You connect your bank once during creator onboarding.</p>
          </details>
        </div>
      </section>

      <section className="lp-cta-final">
        <h2>Ready to claim your wall?</h2>
        <Link to="/login" className="lp-btn-xl">Claim your wall</Link>
        <div className="lp-trust" style={{ marginTop: 18 }}>Free to set up · 4.9% per tip · Cancel anytime</div>
      </section>

    </div>
  );
}
